import { useEffect, useRef, useState, useCallback } from "react";
import { sfx } from "../../lib/soundEngine";

const CELL = 20;
const GRID = 20; // 20x20 grid → 400px canvas
const TICK_MS = 130;

const DIRS = {
    up: { x: 0, y: -1 },
    down: { x: 0, y: 1 },
    left: { x: -1, y: 0 },
    right: { x: 1, y: 0 },
};

const OPPOSITE = { up: "down", down: "up", left: "right", right: "left" };

const randomFood = (snake) => {
    while (true) {
        const p = {
            x: Math.floor(Math.random() * GRID),
            y: Math.floor(Math.random() * GRID),
        };
        if (!snake.some((s) => s.x === p.x && s.y === p.y)) return p;
    }
};

export const SnakeGame = () => {
    const canvasRef = useRef(null);
    const stateRef = useRef({
        snake: [{ x: 10, y: 10 }],
        dir: "right",
        pendingDir: "right",
        food: { x: 5, y: 5 },
        alive: true,
        score: 0,
    });
    const [score, setScore] = useState(0);
    const [best, setBest] = useState(0);
    const [running, setRunning] = useState(false);
    const [gameOver, setGameOver] = useState(false);

    const reset = useCallback(() => {
        stateRef.current = {
            snake: [{ x: 10, y: 10 }],
            dir: "right",
            pendingDir: "right",
            food: randomFood([{ x: 10, y: 10 }]),
            alive: true,
            score: 0,
        };
        setScore(0);
        setGameOver(false);
        setRunning(true);
        sfx.coin();
    }, []);

    const changeDir = useCallback((next) => {
        const s = stateRef.current;
        if (!s.alive) return;
        if (OPPOSITE[s.dir] === next) return;
        s.pendingDir = next;
    }, []);

    useEffect(() => {
        const handler = (e) => {
            const key = e.key.toLowerCase();
            if (["arrowup", "w"].includes(key)) { changeDir("up"); e.preventDefault(); }
            else if (["arrowdown", "s"].includes(key)) { changeDir("down"); e.preventDefault(); }
            else if (["arrowleft", "a"].includes(key)) { changeDir("left"); e.preventDefault(); }
            else if (["arrowright", "d"].includes(key)) { changeDir("right"); e.preventDefault(); }
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [changeDir]);

    const drawFrame = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        const size = GRID * CELL;
        ctx.fillStyle = "#0d0d18";
        ctx.fillRect(0, 0, size, size);
        // subtle grid
        ctx.strokeStyle = "rgba(43, 27, 84, 0.6)";
        ctx.lineWidth = 1;
        for (let i = 0; i <= GRID; i++) {
            ctx.beginPath();
            ctx.moveTo(i * CELL, 0);
            ctx.lineTo(i * CELL, size);
            ctx.stroke();
            ctx.beginPath();
            ctx.moveTo(0, i * CELL);
            ctx.lineTo(size, i * CELL);
            ctx.stroke();
        }

        const { snake, food } = stateRef.current;
        // food
        ctx.fillStyle = "#ff007f";
        ctx.shadowColor = "#ff007f";
        ctx.shadowBlur = 14;
        ctx.fillRect(food.x * CELL + 3, food.y * CELL + 3, CELL - 6, CELL - 6);

        // snake
        ctx.shadowColor = "#39ff14";
        snake.forEach((seg, idx) => {
            ctx.shadowBlur = idx === 0 ? 18 : 10;
            ctx.fillStyle = idx === 0 ? "#7bff53" : "#39ff14";
            ctx.fillRect(seg.x * CELL + 1, seg.y * CELL + 1, CELL - 2, CELL - 2);
        });
        ctx.shadowBlur = 0;
    }, []);

    useEffect(() => {
        drawFrame();
    }, [drawFrame]);

    useEffect(() => {
        if (!running) return;
        const interval = setInterval(() => {
            const s = stateRef.current;
            if (!s.alive) return;
            s.dir = s.pendingDir;
            const head = s.snake[0];
            const d = DIRS[s.dir];
            const next = { x: head.x + d.x, y: head.y + d.y };
            const collideWall =
                next.x < 0 || next.x >= GRID || next.y < 0 || next.y >= GRID;
            const collideSelf = s.snake.some((seg) => seg.x === next.x && seg.y === next.y);
            if (collideWall || collideSelf) {
                s.alive = false;
                setRunning(false);
                setGameOver(true);
                sfx.gameOver();
                setBest((prev) => (s.score > prev ? s.score : prev));
                drawFrame();
                return;
            }
            const ate = next.x === s.food.x && next.y === s.food.y;
            const newSnake = [next, ...s.snake];
            if (!ate) newSnake.pop();
            else {
                s.food = randomFood(newSnake);
                s.score += 1;
                setScore(s.score);
                sfx.eat();
            }
            s.snake = newSnake;
            drawFrame();
        }, TICK_MS);
        return () => clearInterval(interval);
    }, [running, drawFrame]);

    return (
        <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between font-pixel text-xl">
                <span className="neon-lime" data-testid="snake-score">SCORE: {score}</span>
                <span className="neon-amber" data-testid="snake-best">BEST: {best}</span>
            </div>
            <div className="relative mx-auto" style={{ width: "100%", maxWidth: 400 }}>
                <canvas
                    ref={canvasRef}
                    width={GRID * CELL}
                    height={GRID * CELL}
                    data-testid="snake-canvas"
                    className="w-full h-auto rounded-md border-2"
                    style={{ borderColor: "var(--neon-lime)", boxShadow: "0 0 18px rgba(57,255,20,0.35)" }}
                />
                {(!running || gameOver) && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/70 rounded-md">
                        <p className="font-pixel text-3xl neon-lime mb-1">
                            {gameOver ? "GAME OVER" : "READY?"}
                        </p>
                        {gameOver && (
                            <p className="font-pixel text-lg neon-amber mb-3">
                                Final: {score}
                            </p>
                        )}
                        <button onClick={reset} data-testid="snake-start-btn" className="btn-neon btn-neon-lime">
                            {gameOver ? "Retry" : "Start"}
                        </button>
                    </div>
                )}
            </div>

            {/* D-Pad */}
            <div className="grid grid-cols-3 gap-2 mx-auto mt-2" style={{ width: 200 }}>
                <div />
                <button aria-label="Up" data-testid="snake-dpad-up" className="dpad-btn" onClick={() => changeDir("up")}>
                    <i className="fa-solid fa-caret-up" />
                </button>
                <div />
                <button aria-label="Left" data-testid="snake-dpad-left" className="dpad-btn" onClick={() => changeDir("left")}>
                    <i className="fa-solid fa-caret-left" />
                </button>
                <button aria-label="Down" data-testid="snake-dpad-down" className="dpad-btn" onClick={() => changeDir("down")}>
                    <i className="fa-solid fa-caret-down" />
                </button>
                <button aria-label="Right" data-testid="snake-dpad-right" className="dpad-btn" onClick={() => changeDir("right")}>
                    <i className="fa-solid fa-caret-right" />
                </button>
            </div>
            <p className="text-xs text-gray-500 text-center font-body">
                Arrow keys / WASD or use the D-pad
            </p>
        </div>
    );
};
